import {
	Download,
	FolderGit2,
	GitBranch,
	HardDrive,
	Info,
	Monitor,
	Palette,
	Plus,
	Settings,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { WithContext as ReactTags } from "react-tag-input";
import {
	DIFF_OPEN_IN_NEW_WINDOW_STORAGE_KEY,
	DIFF_VIEWER_PRESENTATION,
	DIFF_VIEWER_PRESENTATION_SELECTED_STORAGE_KEY,
	DIFF_VIEWER_PRESENTATION_STORAGE_KEY,
	VIEW_MODE,
} from "../constants";
import { useLocalStorage } from "../hooks/useLocalStorage";
import {
	desktopBackend,
	exportSettings,
	getMigrationTimestamp,
	getStoragePath,
	importSettings,
	isDesktop,
	migrateLocalStorageToDisk,
	revealStorageFile,
} from "../nativeStorage";
import { DEFAULT_REPO_PATHS } from "../utils";
import { CollapsibleSection } from "./CollapsibleSection";
import { ThemeSelector } from "./ThemeSelector";

export const SettingsModal = ({
	isOpen,
	onClose,
	showDrafts,
	showBotPRs,
	onToggleDrafts,
	onToggleBotPRs,
	showPendingWithProjectRules,
	onTogglePendingWithProjectRules,
	showPendingWithAllApprovals,
	onTogglePendingWithAllApprovals,
	greyOutBlockingComments,
	onToggleGreyOutBlockingComments,
	enableCommentsExpansion,
	onToggleCommentsExpansion,
	viewMode,
	showSwimlaneMergedColumn,
	onToggleShowSwimlaneMergedColumn,
	showSwimlaneDeletedColumn,
	onToggleShowSwimlaneDeletedColumn,
	useLocalTime,
	onToggleUseLocalTime,
	watchProjects,
	setWatchProjects,
	excludeProjects,
	setExcludeProjects,
	swimlaneHideCreatedDaysThreshold,
	setSwimlaneHideCreatedDaysThreshold,
	swimlaneHideUpdatedDaysThreshold,
	setSwimlaneHideUpdatedDaysThreshold,
}) => {
	const [enableShortcut, setenableShortcut] = useLocalStorage(
		"enableShortcut",
		true,
	);
	const [repoPaths, setRepoPaths] = useLocalStorage(
		"repoPaths",
		DEFAULT_REPO_PATHS,
	);
	const [openDiffInNewWindow, setOpenDiffInNewWindow] = useLocalStorage(
		DIFF_OPEN_IN_NEW_WINDOW_STORAGE_KEY,
		false,
	);
	const [diffViewerPresentation, setStoredDiffViewerPresentation] =
		useLocalStorage(
			DIFF_VIEWER_PRESENTATION_STORAGE_KEY,
			DIFF_VIEWER_PRESENTATION.FULLSCREEN,
		);
	const [, setDiffViewerPresentationSelected] =
		useLocalStorage(DIFF_VIEWER_PRESENTATION_SELECTED_STORAGE_KEY, false);
	const resolvedDiffViewerPresentation = diffViewerPresentation;
	const modalRef = useRef(null);

	const updateRepoPath = (index, field, value) => {
		setRepoPaths((prev) =>
			prev.map((entry, i) =>
				i === index ? { ...entry, [field]: value } : entry,
			),
		);
	};
	const addRepoPath = () => {
		setRepoPaths((prev) => [...prev, { repo: "", path: "" }]);
	};
	const removeRepoPath = (index) => {
		setRepoPaths((prev) => prev.filter((_, i) => i !== index));
	};
	const setDiffViewerPresentation = (nextPresentation) => {
		setDiffViewerPresentationSelected(true);
		setStoredDiffViewerPresentation(
			nextPresentation === DIFF_VIEWER_PRESENTATION.FULLSCREEN
				? DIFF_VIEWER_PRESENTATION.FULLSCREEN
				: DIFF_VIEWER_PRESENTATION.MODAL,
		);
	};

	// Handle click outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (modalRef.current && !modalRef.current.contains(e.target)) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			// Lock scroll when modal is open
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			// Restore scroll when modal closes
			document.body.style.overflow = "auto";
		};
	}, [isOpen, onClose]);

	// Focus trap and scroll to top
	useEffect(() => {
		if (isOpen && modalRef.current) {
			// Scroll modal content to top
			const scrollableContent =
				modalRef.current.querySelector(".overflow-y-auto");
			if (scrollableContent) {
				scrollableContent.scrollTop = 0;
			}

			// Auto-focus first interactive element (for keyboard users)
			const focusableElements = modalRef.current.querySelectorAll(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			);

			if (focusableElements?.length) {
				focusableElements[0].focus();
			}
		}
	}, [isOpen]);

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 animate-in fade-in duration-200"
			role="dialog"
			aria-modal="true"
			aria-labelledby="settings-title"
		>
			<div
				ref={modalRef}
				className="bg-[var(--color-bg-primary)] rounded-xl shadow-xl w-full max-w-md mx-4 p-6 animate-in zoom-in-95 duration-200"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex justify-between items-center mb-6">
					<h2
						id="settings-title"
						className="text-xl font-semibold text-[var(--color-text-primary)]"
					>
						Settings
					</h2>
					<button
						onClick={onClose}
						className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] p-1 rounded-full hover:bg-[var(--color-bg-secondary)] transition-colors"
						aria-label="Close settings"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				<div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
					<CollapsibleSection
						title="Display Options"
						icon={Monitor}
						iconColor="text-[var(--color-accent)]"
						storageKey="settings-display"
					>
						<div className="space-y-4">
							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showDrafts"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show draft merge requests
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showDrafts"
										checked={showDrafts}
										onChange={() => onToggleDrafts(!showDrafts)}
										className="sr-only"
										aria-checked={showDrafts}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											showDrafts
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() => onToggleDrafts(!showDrafts)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											showDrafts ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleDrafts(!showDrafts)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showBotPRs"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show bot-created merge requests
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showBotPRs"
										checked={showBotPRs}
										onChange={() => onToggleBotPRs(!showBotPRs)}
										className="sr-only"
										aria-checked={showBotPRs}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											showBotPRs
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() => onToggleBotPRs(!showBotPRs)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											showBotPRs ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleBotPRs(!showBotPRs)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="greyOutBlockingComments"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Grey out MRs with blocking comments
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="greyOutBlockingComments"
										checked={greyOutBlockingComments}
										onChange={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										className="sr-only"
										aria-checked={greyOutBlockingComments}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											greyOutBlockingComments
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											greyOutBlockingComments
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onToggleGreyOutBlockingComments(!greyOutBlockingComments)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="enableCommentsExpansion"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Enable comments expansion
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="enableCommentsExpansion"
										checked={enableCommentsExpansion}
										onChange={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										className="sr-only"
										aria-checked={enableCommentsExpansion}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											enableCommentsExpansion
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											enableCommentsExpansion
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onToggleCommentsExpansion(!enableCommentsExpansion)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="useLocalTime"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Display times in local timezone
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="useLocalTime"
										checked={useLocalTime}
										onChange={() => onToggleUseLocalTime(!useLocalTime)}
										className="sr-only"
										aria-checked={useLocalTime}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											useLocalTime
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() => onToggleUseLocalTime(!useLocalTime)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											useLocalTime ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => onToggleUseLocalTime(!useLocalTime)}
										role="presentation"
									/>
								</div>
							</div>

							<div className="py-2">
								<div className="flex flex-col space-y-3">
									<div
										className="text-sm font-medium text-[var(--color-text-primary)]"
										id="diff-viewer-presentation-label"
									>
										Diff viewer default
									</div>
									<div
										className="flex space-x-6"
										role="radiogroup"
										aria-labelledby="diff-viewer-presentation-label"
									>
										<label className="inline-flex items-center">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4"
												name="diffViewerPresentation"
												value={DIFF_VIEWER_PRESENTATION.MODAL}
												checked={
													resolvedDiffViewerPresentation ===
													DIFF_VIEWER_PRESENTATION.MODAL
												}
												onChange={() =>
													setDiffViewerPresentation(
														DIFF_VIEWER_PRESENTATION.MODAL,
													)
												}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Window
											</span>
										</label>
										<label className="inline-flex items-center">
											<input
												type="radio"
												className="form-radio text-[var(--color-accent)] h-4 w-4"
												name="diffViewerPresentation"
												value={DIFF_VIEWER_PRESENTATION.FULLSCREEN}
												checked={
													resolvedDiffViewerPresentation ===
													DIFF_VIEWER_PRESENTATION.FULLSCREEN
												}
												onChange={() =>
													setDiffViewerPresentation(
														DIFF_VIEWER_PRESENTATION.FULLSCREEN,
													)
												}
											/>
											<span className="ml-2 text-sm text-[var(--color-text-primary)]">
												Fullscreen
											</span>
										</label>
									</div>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="openDiffInNewWindow"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Open diff in a new browser window
								</label>
								<label
									htmlFor="openDiffInNewWindow"
									className="relative inline-block w-12 align-middle select-none"
								>
									<input
										type="checkbox"
										id="openDiffInNewWindow"
										checked={openDiffInNewWindow}
										onChange={() =>
											setOpenDiffInNewWindow(!openDiffInNewWindow)
										}
										className="sr-only"
										aria-checked={openDiffInNewWindow}
									/>
									<span
										className={`block w-12 h-7 rounded-full transition-colors ${
											openDiffInNewWindow
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
									/>
									<span
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											openDiffInNewWindow ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
									/>
								</label>
							</div>

							{viewMode === VIEW_MODE.SWIMLANE && (
								<div className="ml-4 mt-3 pt-3 space-y-2 border-t border-[var(--color-border)]">
									<div className="flex items-center justify-between">
										<label
											htmlFor="showSwimlaneMergedColumn"
											className="text-sm text-[var(--color-text-secondary)]"
										>
											Show merged column
										</label>
										<div className="relative inline-block w-12 align-middle select-none">
											<input
												type="checkbox"
												id="showSwimlaneMergedColumn"
												checked={showSwimlaneMergedColumn}
												onChange={() =>
													onToggleShowSwimlaneMergedColumn(
														!showSwimlaneMergedColumn,
													)
												}
												className="sr-only"
												aria-checked={showSwimlaneMergedColumn}
											/>
											<div
												className={`block w-12 h-7 rounded-full transition-colors ${
													showSwimlaneMergedColumn
														? "bg-[var(--color-action)]"
														: "bg-gray-300 dark:bg-gray-600"
												}`}
												onClick={() =>
													onToggleShowSwimlaneMergedColumn(
														!showSwimlaneMergedColumn,
													)
												}
												role="presentation"
											/>
											<div
												className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
													showSwimlaneMergedColumn
														? "translate-x-5"
														: "translate-x-0"
												} shadow-sm`}
												onClick={() =>
													onToggleShowSwimlaneMergedColumn(
														!showSwimlaneMergedColumn,
													)
												}
												role="presentation"
											/>
										</div>
									</div>

									<div className="flex items-center justify-between">
										<label
											htmlFor="showSwimlaneDeletedColumn"
											className="text-sm text-[var(--color-text-secondary)]"
										>
											Show deleted column
										</label>
										<div className="relative inline-block w-12 align-middle select-none">
											<input
												type="checkbox"
												id="showSwimlaneDeletedColumn"
												checked={showSwimlaneDeletedColumn}
												onChange={() =>
													onToggleShowSwimlaneDeletedColumn(
														!showSwimlaneDeletedColumn,
													)
												}
												className="sr-only"
												aria-checked={showSwimlaneDeletedColumn}
											/>
											<div
												className={`block w-12 h-7 rounded-full transition-colors ${
													showSwimlaneDeletedColumn
														? "bg-[var(--color-action)]"
														: "bg-gray-300 dark:bg-gray-600"
												}`}
												onClick={() =>
													onToggleShowSwimlaneDeletedColumn(
														!showSwimlaneDeletedColumn,
													)
												}
												role="presentation"
											/>
											<div
												className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
													showSwimlaneDeletedColumn
														? "translate-x-5"
														: "translate-x-0"
												} shadow-sm`}
												onClick={() =>
													onToggleShowSwimlaneDeletedColumn(
														!showSwimlaneDeletedColumn,
													)
												}
												role="presentation"
											/>
										</div>
									</div>

									<div className="mt-4 space-y-3 pt-3 border-t border-[var(--color-border)]">
										<div className="text-xs text-[var(--color-text-secondary)] mb-2">
											Auto-hide MRs when <strong>both</strong> conditions are
											met:
										</div>
										<div>
											<label
												htmlFor="swimlaneHideCreatedDaysThreshold"
												className="text-sm text-[var(--color-text-secondary)] block mb-1"
											>
												Created more than (days ago)
											</label>
											<input
												type="number"
												id="swimlaneHideCreatedDaysThreshold"
												min="1"
												value={swimlaneHideCreatedDaysThreshold}
												onChange={(e) =>
													setSwimlaneHideCreatedDaysThreshold(
														Number.parseInt(e.target.value) || 30,
													)
												}
												className="w-full px-3 py-2 text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
											/>
										</div>
										<div>
											<label
												htmlFor="swimlaneHideUpdatedDaysThreshold"
												className="text-sm text-[var(--color-text-secondary)] block mb-1"
											>
												Not updated in (days)
											</label>
											<input
												type="number"
												id="swimlaneHideUpdatedDaysThreshold"
												min="1"
												value={swimlaneHideUpdatedDaysThreshold}
												onChange={(e) =>
													setSwimlaneHideUpdatedDaysThreshold(
														Number.parseInt(e.target.value) || 14,
													)
												}
												className="w-full px-3 py-2 text-sm bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					</CollapsibleSection>

					<CollapsibleSection
						title="Behavior"
						icon={Settings}
						iconColor="text-green-500"
						storageKey="settings-behavior"
					>
						<div className="space-y-4">
							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showPendingWithProjectRules"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show pending merge requests with project rule blockers
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showPendingWithProjectRules"
										checked={showPendingWithProjectRules}
										onChange={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										className="sr-only"
										aria-checked={showPendingWithProjectRules}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											showPendingWithProjectRules
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											showPendingWithProjectRules
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onTogglePendingWithProjectRules(
												!showPendingWithProjectRules,
											)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="showPendingWithAllApprovals"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show pending merge requests with all approvals
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="showPendingWithAllApprovals"
										checked={showPendingWithAllApprovals}
										onChange={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										className="sr-only"
										aria-checked={showPendingWithAllApprovals}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											showPendingWithAllApprovals
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											showPendingWithAllApprovals
												? "translate-x-5"
												: "translate-x-0"
										} shadow-sm`}
										onClick={() =>
											onTogglePendingWithAllApprovals(
												!showPendingWithAllApprovals,
											)
										}
										role="presentation"
									/>
								</div>
							</div>

							<div className="flex flex-col space-y-3">
								<label
									htmlFor="watchProjects"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Show all MRs in these projects
								</label>
								<div className="flex space-x-6 mt-1">
									<ReactTags
										tags={watchProjects}
										handleAddition={(tag) =>
											setWatchProjects((prev) => [...prev, tag])
										}
										handleDelete={(idx) =>
											setWatchProjects(
												watchProjects.filter((_, i) => i !== idx),
											)
										}
										name="watchProjects"
										id="watchProjects"
										allowDragDrop={false}
										placeholder="e.g. cloudflare/fl/fl2"
										inputFieldPosition="bottom"
										maxTags={50}
										// TODO: suggestions/handleFilterSuggestions
									/>
								</div>
							</div>

							<div className="flex flex-col space-y-3">
								<label
									htmlFor="excludeProjects"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Exclude MRs in these projects
								</label>
								<div className="flex space-x-6 mt-1">
									<ReactTags
										tags={excludeProjects}
										handleAddition={(tag) =>
											setExcludeProjects((prev) => [...prev, tag])
										}
										handleDelete={(idx) =>
											setExcludeProjects(
												excludeProjects.filter((_, i) => i !== idx),
											)
										}
										name="excludeProjects"
										id="excludeProjects"
										allowDragDrop={false}
										placeholder="e.g. cloudflare/fl/fl2"
										inputFieldPosition="bottom"
										maxTags={50}
									/>
								</div>
							</div>

							<div className="flex items-center justify-between py-2">
								<label
									htmlFor="enableShortcut"
									className="text-sm font-medium text-[var(--color-text-primary)]"
								>
									Enable keyboard shortcut
									<span className="ml-2 text-xs bg-[var(--color-bg-secondary)] px-2 py-1 rounded text-[var(--color-text-secondary)]">
										⌘F for search
									</span>
								</label>
								<div className="relative inline-block w-12 align-middle select-none">
									<input
										type="checkbox"
										id="enableShortcut"
										checked={enableShortcut}
										onChange={() => setenableShortcut(!enableShortcut)}
										className="sr-only"
										aria-checked={enableShortcut}
									/>
									<div
										className={`block w-12 h-7 rounded-full transition-colors ${
											enableShortcut
												? "bg-[var(--color-action)]"
												: "bg-gray-300 dark:bg-gray-600"
										}`}
										onClick={() => setenableShortcut(!enableShortcut)}
										role="presentation"
									/>
									<div
										className={`absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform transform ${
											enableShortcut ? "translate-x-5" : "translate-x-0"
										} shadow-sm`}
										onClick={() => setenableShortcut(!enableShortcut)}
										role="presentation"
									/>
								</div>
							</div>
						</div>
					</CollapsibleSection>

					<CollapsibleSection
						title="Repo Paths"
						icon={FolderGit2}
						iconColor="text-[var(--color-accent)]"
						storageKey="settings-repo-paths"
					>
						<div className="space-y-3">
							<div className="flex items-start gap-2">
								<Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--color-text-secondary)]" />
								<p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
									Map each repo to its local clone path. The{" "}
									<GitBranch className="inline w-3.5 h-3.5 -mt-0.5" /> button on
									a merge request copies a{" "}
									<code className="px-1 rounded bg-[var(--color-bg-secondary)]">
										git checkout
									</code>{" "}
									command for its branch in the matching path. The repo name
									must match the last segment of the project path (e.g.{" "}
									<code className="px-1 rounded bg-[var(--color-bg-secondary)]">
										r2-core
									</code>
									).
								</p>
							</div>

							<div className="space-y-2">
								{repoPaths.map((entry, index) => (
									<div key={index} className="flex items-center gap-2">
										<input
											type="text"
											value={entry.repo}
											onChange={(e) =>
												updateRepoPath(index, "repo", e.target.value)
											}
											placeholder="repo name"
											aria-label="Repo name"
											className="w-1/3 px-2 py-1.5 text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
										/>
										<input
											type="text"
											value={entry.path}
											onChange={(e) =>
												updateRepoPath(index, "path", e.target.value)
											}
											placeholder="/absolute/local/path"
											aria-label="Local path"
											className="flex-1 px-2 py-1.5 text-xs bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]"
										/>
										<button
											type="button"
											onClick={() => removeRepoPath(index)}
											aria-label="Remove repo path"
											className="p-1.5 rounded text-[var(--color-text-secondary)] hover:text-red-500 hover:bg-[var(--color-bg-secondary)] transition-colors"
										>
											<Trash2 className="w-4 h-4" />
										</button>
									</div>
								))}
							</div>

							<button
								type="button"
								onClick={addRepoPath}
								className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-accent)] hover:opacity-80 transition-opacity"
							>
								<Plus className="w-4 h-4" />
								Add repo
							</button>
						</div>
					</CollapsibleSection>

					{isDesktop() && <DesktopStorageSection />}

					<CollapsibleSection
						title="Theme"
						icon={Palette}
						iconColor="text-[var(--color-accent)]"
						storageKey="settings-theme"
					>
						<div className="flex flex-col space-y-3">
							<div
								className="text-sm font-medium text-[var(--color-text-primary)]"
								id="theme-group-label"
							>
								Choose theme:
							</div>
							<ThemeSelector />
						</div>
					</CollapsibleSection>
				</div>

				<div className="mt-8 flex justify-end">
					<button
						onClick={onClose}
						className="px-5 py-2 bg-[var(--color-action)] text-[var(--color-action-text)] rounded-lg text-sm font-medium hover:bg-[var(--color-action-hover)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-2"
					>
						Close
					</button>
				</div>
			</div>
		</div>
	);
};

const formatTimestamp = (ts) => {
	if (!ts) return null;
	try {
		return new Date(ts).toLocaleString();
	} catch {
		return null;
	}
};

const DesktopStorageSection = () => {
	const backend = desktopBackend();
	const [storagePath, setStoragePath] = useState(null);
	const [migrationTime, setMigrationTime] = useState(() =>
		getMigrationTimestamp(),
	);
	const [status, setStatus] = useState(null);
	const [busy, setBusy] = useState(false);
	const fileInputRef = useRef(null);

	useEffect(() => {
		let cancelled = false;
		getStoragePath()
			.then((p) => {
				if (!cancelled) setStoragePath(p);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const showStatus = (kind, message) => {
		setStatus({ kind, message });
		setTimeout(() => setStatus(null), 4000);
	};

	const handleMigrate = async () => {
		setBusy(true);
		try {
			const result = await migrateLocalStorageToDisk();
			setMigrationTime(getMigrationTimestamp());
			showStatus(
				"success",
				`Migrated ${result.migrated} setting${result.migrated === 1 ? "" : "s"} to disk.`,
			);
		} catch (error) {
			console.error(error);
			showStatus("error", `Migration failed: ${error?.message || error}`);
		} finally {
			setBusy(false);
		}
	};

	const handleExport = async () => {
		setBusy(true);
		try {
			const data = await exportSettings();
			const blob = new Blob([JSON.stringify(data, null, 2)], {
				type: "application/json",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `gitlab-dash-settings-${new Date().toISOString().slice(0, 10)}.json`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
			showStatus("success", "Exported settings.");
		} catch (error) {
			console.error(error);
			showStatus("error", `Export failed: ${error?.message || error}`);
		} finally {
			setBusy(false);
		}
	};

	const handleImportFile = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) return;
		setBusy(true);
		try {
			const text = await file.text();
			const parsed = JSON.parse(text);
			const result = await importSettings(parsed);
			showStatus(
				"success",
				`Imported ${result.imported} setting${result.imported === 1 ? "" : "s"}. Reload for all views to pick up changes.`,
			);
		} catch (error) {
			console.error(error);
			showStatus("error", `Import failed: ${error?.message || error}`);
		} finally {
			setBusy(false);
		}
	};

	const handleReveal = async () => {
		try {
			await revealStorageFile();
		} catch (error) {
			console.error(error);
		}
	};

	const migratedLabel = formatTimestamp(migrationTime);

	return (
		<CollapsibleSection
			title="Desktop Storage"
			icon={HardDrive}
			iconColor="text-[var(--color-accent)]"
			storageKey="settings-desktop-storage"
		>
			<div className="space-y-4">
				<div className="text-sm text-[var(--color-text-secondary)]">
					Running the {backend === "electron" ? "Electron" : "Tauri"} desktop
					app. Settings are mirrored to a JSON file on disk so they survive
					cache clears and follow you if you copy the file.
				</div>

				{storagePath && (
					<div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2 text-xs font-mono break-all text-[var(--color-text-primary)]">
						{storagePath}
					</div>
				)}

				<div className="text-xs text-[var(--color-text-secondary)]">
					{migratedLabel
						? `Last migrated from localStorage: ${migratedLabel}`
						: "Not yet migrated. Click below to copy every persisted setting from browser storage to disk."}
				</div>

				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						onClick={handleMigrate}
						disabled={busy}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-action)] text-[var(--color-action-text)] text-sm font-medium hover:bg-[var(--color-action-hover)] transition-colors disabled:opacity-50"
					>
						<HardDrive className="w-4 h-4" />
						{migratedLabel ? "Re-sync to disk" : "Migrate to disk"}
					</button>
					<button
						type="button"
						onClick={handleExport}
						disabled={busy}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors disabled:opacity-50"
					>
						<Download className="w-4 h-4" />
						Export
					</button>
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						disabled={busy}
						className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors disabled:opacity-50"
					>
						<Upload className="w-4 h-4" />
						Import
					</button>
					{backend === "electron" && (
						<button
							type="button"
							onClick={handleReveal}
							disabled={busy}
							className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors disabled:opacity-50"
						>
							<FolderGit2 className="w-4 h-4" />
							Reveal file
						</button>
					)}
				</div>

				<input
					ref={fileInputRef}
					type="file"
					accept="application/json,.json"
					className="hidden"
					onChange={handleImportFile}
				/>

				{status && (
					<div
						className={`text-xs rounded-md px-3 py-2 ${
							status.kind === "success"
								? "bg-green-500/10 text-green-500"
								: "bg-red-500/10 text-red-500"
						}`}
					>
						{status.message}
					</div>
				)}
			</div>
		</CollapsibleSection>
	);
};
